import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/services/api';
import { Platform } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

// Mock data - in a real app, this would come from the API
const tempEmailPlatforms = ['10MinuteMail', 'Guerrilla Mail', 'TempMail'];

export default function PlatformSelectionPage() {
  const [platforms, setPlatforms] = useState<Platform[]>([]);
  const [selectedTempEmail, setSelectedTempEmail] = useState('');
  const [selectedCaptchaPlatforms, setSelectedCaptchaPlatforms] = useState<string[]>([]);
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [payoutCurrency, setPayoutCurrency] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get('/platforms').then(res => setPlatforms(res.data));
  }, []);

  const handleCaptchaSelection = (platformId: string) => {
    setSelectedCaptchaPlatforms(prev =>
      prev.includes(platformId) ? prev.filter(id => id !== platformId) : [...prev, platformId]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const payload = {
        tempEmailPlatform: selectedTempEmail,
        fullName,
        password,
        captchaPlatforms: selectedCaptchaPlatforms,
        payoutCurrency,
        walletAddress,
      };
      await api.post('/platforms/setup', payload);
      alert('Setup initiated! The app is now creating accounts and starting the solver.');
      navigate('/solver');
    } catch (error) {
      alert('Failed to start setup.');
    } finally {
      setIsLoading(false);
    }
  };

  // Group platforms by payment type
  const cryptoPlatforms = platforms.filter(p => p.paymentType === 'crypto');
  const paypalPlatforms = platforms.filter(p => p.paymentType === 'paypal');
  const payoneerPlatforms = platforms.filter(p => p.paymentType === 'payoneer');
  const otherPlatforms = platforms.filter(p => !['crypto', 'paypal', 'payoneer'].includes(p.paymentType));

  const availableCurrencies = [...new Set(
    platforms
      .filter(p => selectedCaptchaPlatforms.includes(p.id))
      .flatMap(p => p.supportedCryptos)
  )];

  return (
    <form onSubmit={handleSubmit} className="container mx-auto p-8 space-y-8">
      <h1 className="text-3xl font-bold">Platform Setup</h1>

      <div>
        <h2 className="text-2xl mb-4">1. Choose Temporary Email Provider</h2>
        <div className="grid grid-cols-3 gap-4">
          {tempEmailPlatforms.map(platform => (
            <Button key={platform} type="button" variant={selectedTempEmail === platform ? "default" : "outline"} onClick={() => setSelectedTempEmail(platform)}>
              {platform}
            </Button>
          ))}
        </div>
      </div>

      <div>
        <h2 className="text-2xl mb-4">2. Basic Account Information</h2>
        <Input placeholder="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} className="mb-4" />
        <Input type="password" placeholder="Master Password" value={password} onChange={(e) => setPassword(e.target.value)} className="mb-4" />
      </div>

      <div>
        <h2 className="text-2xl mb-4">3. Select CAPTCHA Platforms</h2>
        
        {/* Crypto Platforms */}
        {cryptoPlatforms.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">💰 Cryptocurrency Platforms</h3>
            <div className="grid grid-cols-2 gap-4">
              {cryptoPlatforms.map(platform => (
                <div key={platform.id} className={`p-4 rounded-lg border-2 cursor-pointer ${selectedCaptchaPlatforms.includes(platform.id) ? 'border-green-500 bg-green-900' : 'border-gray-600 bg-gray-800'}`} onClick={() => handleCaptchaSelection(platform.id)}>
                  <h3 className="font-bold">{platform.name}</h3>
                  <p className="text-sm text-gray-400">Payment: {platform.paymentType}</p>
                  <p className="text-sm text-gray-400">Supports: {platform.supportedCryptos.join(', ')}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* PayPal Platforms */}
        {paypalPlatforms.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">💳 PayPal Platforms</h3>
            <div className="grid grid-cols-2 gap-4">
              {paypalPlatforms.map(platform => (
                <div key={platform.id} className={`p-4 rounded-lg border-2 cursor-pointer ${selectedCaptchaPlatforms.includes(platform.id) ? 'border-green-500 bg-green-900' : 'border-gray-600 bg-gray-800'}`} onClick={() => handleCaptchaSelection(platform.id)}>
                  <h3 className="font-bold">{platform.name}</h3>
                  <p className="text-sm text-gray-400">Payment: {platform.paymentType}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Payoneer Platforms */}
        {payoneerPlatforms.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">💵 Payoneer Platforms</h3>
            <div className="grid grid-cols-2 gap-4">
              {payoneerPlatforms.map(platform => (
                <div key={platform.id} className={`p-4 rounded-lg border-2 cursor-pointer ${selectedCaptchaPlatforms.includes(platform.id) ? 'border-green-500 bg-green-900' : 'border-gray-600 bg-gray-800'}`} onClick={() => handleCaptchaSelection(platform.id)}>
                  <h3 className="font-bold">{platform.name}</h3>
                  <p className="text-sm text-gray-400">Payment: {platform.paymentType}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Other Platforms */}
        {otherPlatforms.length > 0 && (
          <div className="mb-6">
            <h3 className="text-xl font-semibold mb-3 text-blue-400">💼 Other Payment Methods</h3>
            <div className="grid grid-cols-2 gap-4">
              {otherPlatforms.map(platform => (
                <div key={platform.id} className={`p-4 rounded-lg border-2 cursor-pointer ${selectedCaptchaPlatforms.includes(platform.id) ? 'border-green-500 bg-green-900' : 'border-gray-600 bg-gray-800'}`} onClick={() => handleCaptchaSelection(platform.id)}>
                  <h3 className="font-bold">{platform.name}</h3>
                  <p className="text-sm text-gray-400">Payment: {platform.paymentType}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="mt-4">
          <Button type="button" variant="outline" onClick={() => {
            if (selectedCaptchaPlatforms.length === platforms.length) {
              setSelectedCaptchaPlatforms([]);
            } else {
              setSelectedCaptchaPlatforms(platforms.map(p => p.id));
            }
          }}>
            {selectedCaptchaPlatforms.length === platforms.length ? 'Deselect All' : 'Select All'}
          </Button>
        </div>
      </div>

      {availableCurrencies.length > 0 && (
        <div>
          <h2 className="text-2xl mb-4">4. Payout Details</h2>
          <div className="mb-4">
            <p className="mb-2">How do you want to get paid?</p>
            <div className="grid grid-cols-2 gap-4 mb-4">
              {availableCurrencies.includes('BTC') && (
                <Button 
                  type="button" 
                  variant={payoutCurrency === 'BTC' ? "default" : "outline"} 
                  onClick={() => setPayoutCurrency('BTC')}
                >
                  Bitcoin (BTC)
                </Button>
              )}
              {availableCurrencies.includes('USDT_TRC20') && (
                <Button 
                  type="button" 
                  variant={payoutCurrency === 'USDT_TRC20' ? "default" : "outline"} 
                  onClick={() => setPayoutCurrency('USDT_TRC20')}
                >
                  USDT (TRC20)
                </Button>
              )}
              {availableCurrencies.includes('USDT_ERC20') && (
                <Button 
                  type="button" 
                  variant={payoutCurrency === 'USDT_ERC20' ? "default" : "outline"} 
                  onClick={() => setPayoutCurrency('USDT_ERC20')}
                >
                  USDT (ERC20)
                </Button>
              )}
            </div>
          </div>
          <Input placeholder="Your Wallet Address" value={walletAddress} onChange={(e) => setWalletAddress(e.target.value)} className="mb-4" />
          
          {payoutCurrency && (
            <div className="p-3 bg-gray-700 rounded">
              <p className="text-sm text-gray-300">
                The following platforms support {payoutCurrency}:
              </p>
              <ul className="list-disc list-inside text-sm text-gray-400 mt-1">
                {platforms
                  .filter(p => selectedCaptchaPlatforms.includes(p.id) && p.supportedCryptos.includes(payoutCurrency))
                  .map(p => <li key={p.id}>{p.name}</li>)
                }
              </ul>
            </div>
          )}
        </div>
      )}

      <Button type="submit" disabled={isLoading || !selectedTempEmail || !fullName || !password || selectedCaptchaPlatforms.length === 0 || !payoutCurrency || !walletAddress} className="w-full">
        {isLoading ? 'Starting...' : 'Complete Setup & Start Solving'}
      </Button>
    </form>
  );
}
